import React, { useState, useEffect } from 'react';
import { 
  Search, Grid, List, RefreshCw, MapPin, Calendar, TrendingUp, 
  Users, Target, CheckCircle, XCircle 
} from 'lucide-react';
import './PreSowing.css';

const GrowthTillering = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState({
    totalActivities: 0,
    landPrepared: 0,
    notPrepared: 0,
    activeFarmers: 0,
    plannedArea: 0
  });

  // ---------------------------------------------------
  // ⭐ FETCH API WITH TOKEN + FILTER SUPPORT
  // ---------------------------------------------------
  const loadGrowthTillering = async () => {
    try {
      const token = localStorage.getItem("authToken");

      let url = " https://sadaatagrofarms.com/api/crop_stages/growth-tillering/";

      // Filters applied
      const params = new URLSearchParams();

      if (searchQuery) params.append("search", searchQuery);
      if (filterStatus !== "All") params.append("status", filterStatus);

      if (params.toString()) url += "?" + params.toString();

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await res.json();

      const list = data.results || data;
      setActivities(list);

      // Auto-Stats
      setStats({
        totalActivities: list.length,
        landPrepared: list.filter(i => i.weeds_present === true).length,
        notPrepared: list.filter(i => i.weeds_present === false).length,
        activeFarmers: [...new Set(list.map(i => i.farmer))].length,
        plannedArea: list.length * 10 // dummy area
      });

    } catch (err) {
      console.error("❌ Error fetching Growth Tillering:", err);
    }
  };

  // Run on first load + whenever filters change
  useEffect(() => {
    loadGrowthTillering();
  }, [searchQuery, filterStatus]);

  return (
    <div className="pre-sowing">

      {/* Stage Tabs */}
      <div className="stage-tabs">
        <button className="stage-tab">Pre-Sowing</button>
        <button className="stage-tab">Sowing & Emergence</button>
        <button className="stage-tab">Emergence</button>
        <button className="stage-tab active">Growth & Tillering</button>
        <button className="stage-tab">Booting Stage</button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Growth & Tillering Dashboard</h1>
          <p className="subtitle">View all growth and tillering stage reports</p>
        </div>
        <button className="refresh-btn" onClick={loadGrowthTillering}>
          <RefreshCw size={16} />
          Refresh Data
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon green"><TrendingUp size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalActivities}</div>
            <div className="stat-label">Total Records</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green"><CheckCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.landPrepared}</div>
            <div className="stat-label">Prepared</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red"><XCircle size={24} /></div>
          <div className="stat-content">
            <div className="stat-value danger">{stats.notPrepared}</div>
            <div className="stat-label">Not Prepared</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon blue"><Users size={24} /></div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeFarmers}</div>
            <div className="stat-label">Unique Farmers</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">

        {/* Search */}
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text"
            placeholder="Search crop, location, farmer..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* View Toggle */}
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={18} />
          </button>

          <button 
            className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            <List size={18} />
          </button>
        </div>

        {/* Status Filter */}
        <div className="status-filters">
          <button className={`filter-chip ${filterStatus === 'All' ? 'active' : ''}`} onClick={() => setFilterStatus('All')}>All</button>
          <button className={`filter-chip ${filterStatus === 'Prepared' ? 'active' : ''}`} onClick={() => setFilterStatus('Prepared')}>Prepared</button>
          <button className={`filter-chip ${filterStatus === 'Not Prepared' ? 'active' : ''}`} onClick={() => setFilterStatus('Not Prepared')}>Not Prepared</button>
        </div>

      </div>

      {/* Activities */}
      <div className={`activities-${viewMode}`}>
        {activities.map((item) => (
          <div className="activity-card" key={item.id}>
            
            <div className="activity-header">
              <span className="crop-name">{item.crop_name}</span>
              <span className={`status-badge ${item.weeds_present ? 'prepared' : 'not-prepared'}`}>
                {item.weeds_present ? "Prepared" : "Not Prepared"}
              </span>
            </div>

            <div className="activity-location">
              <MapPin size={14} />
              <span>Farmer #{item.farmer} • Farm #{item.farm}</span>
            </div>

            <div className="activity-details">
              <div className="detail-row"><span className="detail-label">Weed Type:</span> {item.weed_type}</div>
              <div className="detail-row"><span className="detail-label">Irrigation:</span> {item.irrigation_done ? "Yes" : "No"}</div>
              <div className="detail-row"><span className="detail-label">Fertilizer:</span> {item.fertilizer_applied ? "Applied" : "Not Applied"}</div>
            </div>

            <div className="activity-footer">
              <Calendar size={14} />
              <strong>{new Date(item.created_at).toDateString()}</strong>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default GrowthTillering;
